import { request } from './core'
import type {
  DashboardSummary,
  DashboardTimeSeries,
  DashboardSection,
  DashboardOverviewSection,
  DashboardAgentsSection,
  DashboardWorkflowsSection,
  DashboardEvalsSection,
  DashboardSessionsSection,
  DashboardCostSection,
  DashboardPlatformSection,
} from './types'
import type { MetricsDateRange } from '../../types/metricsDateRange'
import { appendMetricsDateRangeToSearchParams } from '../../types/metricsDateRange'

/** Build query string for dashboard v2; tenant_id is only used for platform admins. */
function dashboardQuery(
  section: string,
  tenantId?: string | null,
  dateRange?: MetricsDateRange,
  search?: string,
  environment?: string,
): string {
  const params = new URLSearchParams({ section })
  if (tenantId) params.set('tenant_id', tenantId)
  if (dateRange) {
    appendMetricsDateRangeToSearchParams(params, dateRange)
  }
  if (search) params.set('search', search)
  if (environment) params.set('environment', environment)
  return `/dashboard/v2?${params.toString()}`
}

export const dashboardApi = {
  getSummary: () => request<DashboardSummary>('/dashboard/summary'),

  getTimeSeries: (metric: 'projects' | 'agents' | 'simulations' = 'projects') =>
    request<DashboardTimeSeries>(`/dashboard/timeseries?metric=${metric}`),

  getOverview: (tenantId?: string | null, dateRange?: MetricsDateRange) =>
    request<DashboardOverviewSection>(dashboardQuery('overview', tenantId, dateRange)),

  getAgents: (tenantId?: string | null, dateRange?: MetricsDateRange) =>
    request<DashboardAgentsSection>(dashboardQuery('agents', tenantId, dateRange)),

  getWorkflows: (tenantId?: string | null, dateRange?: MetricsDateRange) =>
    request<DashboardWorkflowsSection>(dashboardQuery('workflows', tenantId, dateRange)),

  getEvals: (tenantId?: string | null, dateRange?: MetricsDateRange) =>
    request<DashboardEvalsSection>(dashboardQuery('evals', tenantId, dateRange)),

  getSessions: (tenantId?: string | null, dateRange?: MetricsDateRange) =>
    request<DashboardSessionsSection>(dashboardQuery('sessions', tenantId, dateRange)),

  getCost: (
    tenantId?: string | null,
    dateRange?: MetricsDateRange,
    search?: string,
    environment?: string,
  ) =>
    request<DashboardCostSection>(dashboardQuery('cost', tenantId, dateRange, search, environment)),

  getPlatform: (dateRange?: MetricsDateRange) =>
    request<DashboardPlatformSection>(dashboardQuery('platform', undefined, dateRange)),

  getSection: <T>(section: DashboardSection, tenantId?: string | null, dateRange?: MetricsDateRange) =>
    request<T>(dashboardQuery(section, tenantId, dateRange)),
}
